import { Result, ResultAsync, ok, err } from 'neverthrow';

// ─── Error Hierarchy ───────────────────────────────────────────────────────────
//
// DeviceError is a discriminated union of all errors that can occur during
// Modbus communication. The `kind` field enables exhaustive switch statements.
//
// TransportError  → channel is dead (BLE disconnected, socket closed, timeout)
// ProtocolError   → channel works, but the register/value doesn't
// VerificationError → write succeeded but read-back doesn't match
//
// Only TransportErrors should trip the CircuitBreaker and close the channel.
// ProtocolErrors are normal Modbus operation (device doesn't support a register).

/** Error indicating the transport channel is no longer functional. */
export type TransportError = {
  readonly kind: 'transport';
  readonly reason: 'timeout' | 'disconnected' | 'request-failed' | 'overloaded' | 'circuit-open' | 'unreachable';
  readonly deviceId?: string;
  readonly message?: string;
};

/** Error indicating a Modbus protocol-level exception (channel is still functional). */
export type ProtocolError = {
  readonly kind: 'protocol';
  readonly modbusException: number;
  readonly address?: number;
  readonly description: string;
  readonly subResponseIndex?: number;
};

/** Error indicating write verification failed (read-back mismatch). */
export type VerificationError = {
  readonly kind: 'verification';
  readonly registerAddress: number;
  readonly expected: number;
  readonly actual: number;
};

/** Discriminated union of all device communication errors. */
export type DeviceError = TransportError | ProtocolError | VerificationError;

// ─── Modbus Exception Descriptions ────────────────────────────────────────────

const MODBUS_EXCEPTION_DESCRIPTIONS: Record<number, string> = {
  0x01: 'ILLEGAL_FUNCTION',
  0x02: 'ILLEGAL_DATA_ADDRESS',
  0x03: 'ILLEGAL_DATA_VALUE',
  0x04: 'SLAVE_DEVICE_FAILURE',
  0x05: 'ACKNOWLEDGE',
  0x06: 'SLAVE_DEVICE_BUSY',
  0x08: 'MEMORY_PARITY_ERROR',
  0x0A: 'GATEWAY_PATH_UNAVAILABLE',
  0x0B: 'GATEWAY_TARGET_FAILED',
};

export function modbusExceptionDescription(code: number): string {
  return MODBUS_EXCEPTION_DESCRIPTIONS[code] ?? `UNKNOWN_EXCEPTION_0x${code.toString(16).toUpperCase()}`;
}

// ─── Factory Helpers ──────────────────────────────────────────────────────────

export function transportError(
  reason: TransportError['reason'],
  opts?: { deviceId?: string; message?: string },
): TransportError {
  return { kind: 'transport', reason, ...opts };
}

export function protocolError(
  modbusException: number,
  opts?: { address?: number; subResponseIndex?: number },
): ProtocolError {
  return {
    kind: 'protocol',
    modbusException,
    description: modbusExceptionDescription(modbusException),
    ...opts,
  };
}

export function verificationError(
  registerAddress: number,
  expected: number,
  actual: number,
): VerificationError {
  return { kind: 'verification', registerAddress, expected, actual };
}

// ─── Classification ───────────────────────────────────────────────────────────

/** Whether this error should trip the CircuitBreaker (= channel is dead). */
export function isTransportError(error: DeviceError): error is TransportError {
  return error.kind === 'transport';
}

/** Whether a Modbus exception code indicates a transport-level problem (gateway errors). */
export function isGatewayException(modbusException: number): boolean {
  return modbusException === 0x0A || modbusException === 0x0B;
}

/**
 * Whether a protocol error is "skippable" — the register simply doesn't exist
 * on this device/firmware, and the rest of the batch can continue.
 */
export function isSkippableProtocolError(error: ProtocolError): boolean {
  return (
    error.modbusException === 0x01 || // Illegal Function
    error.modbusException === 0x02    // Illegal Data Address
  );
}

// ─── Result Type Aliases ──────────────────────────────────────────────────────

/** Synchronous result with DeviceError. */
export type DeviceResult<T> = Result<T, DeviceError>;

// Re-export neverthrow primitives for convenience
export { Result, ResultAsync, ok, err };
